import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import dayjs from 'dayjs'
import { DateString } from '@/types/utils'
import { DateMessage, MESSAGE_TYPE_TOPLEVEL } from '@/types/message'
import { extractCursor } from '@/api/util'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  messageModal: false,
  history: {
    messages: [],
    nextCursor: ''
  },
  unreadHistory: {
    messages: []
  },
  recipients: []
})

/* -------------------------------------------------------------------------- */
/*      console.log('dateMessages==' + JSON.stringify(dateMessages)) 
console.log('\ndateMessages==' + JSON.stringify(dateMessages))
console.log('\nday==' + day)
*/

const getters: Getters<S, G> = {
  messages: ({ history }) => history.messages,
  unreadMessages: ({ unreadHistory }) => unreadHistory.messages,
  dateMessages: state => {
    const messages = state.history.messages
    const days = messages.reduce((days, message) => {
      const startDate = dayjs(message.registeredDatetime).format('YYYY-MM-DD')
      if (days.indexOf(startDate) < 0) days.push(startDate)
      return days
    }, [] as string[])
    return days.reduce((dateMessages, day) => {
      const date = dayjs(day).format('YYYY-MM-DD') as DateString
      dateMessages.push({
        date,
        messages: messages.filter(
          message =>
            dayjs(message.registeredDatetime).format('YYYY-MM-DD') == date
        )
      })
      return dateMessages
    }, [] as DateMessage[])
  },
  hasHistoryNext: state => {
    return !!state.history.nextCursor
  },
  recipients: state => {
    return state.recipients
  },
  messageModal: state => {
    return state.messageModal
  },
  latestNormalMessageUserId: (state, getters, rootState, rootGetters) => {
    const selfId = rootGetters['user/userInfo'].id
    const normaltypeMessages = state.history.messages.filter(
      message => message.messageTypeLevel < MESSAGE_TYPE_TOPLEVEL
    )
    const selfMessageUserIds = normaltypeMessages.reduce((ids, message) => {
      const selfMessageUsers = message.messageuserSet.filter(
        muser => muser.user.id === selfId && !muser.isRead
      )
      return [...ids, ...selfMessageUsers.map(muser => muser.id)]
    }, [] as number[])
    return selfMessageUserIds
      ? selfMessageUserIds.sort((a, b) => b - a)[0]
      : null
  },
  latestMessageUserId: (state, getters, rootState, rootGetters) => {
    const selfId = rootGetters['user/userInfo'].id
    const selfMessageUserIds = state.history.messages.reduce((ids, message) => {
      const selfMessageUsers = message.messageuserSet.filter(
        muser => muser.user.id === selfId && !muser.isRead
      )
      return [...ids, ...selfMessageUsers.map(muser => muser.id)]
    }, [] as number[])
    return selfMessageUserIds
      ? selfMessageUserIds.sort((a, b) => b - a)[0]
      : null
  }
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  displayMessageModal: (state, display) => {
    state.messageModal = display
  },
  updateHistory: (state, messages) => {
    state.history = {
      ...state.history,
      messages: messages
    }
  },
  updateUnreadHistory: (state, messages) => {
    state.unreadHistory = {
      ...state.unreadHistory,
      messages: messages
    }
  },
  addHistory: (state, messages) => {
    state.history = {
      ...state.history,
      messages: [...state.history.messages, ...messages]
    }
  },
  updateHistoryNext: (state, next) => {
    state.history = {
      ...state.history,
      nextCursor: extractCursor(next)
    }
  },
  updateRecipients: (state, users) => {
    state.recipients = users
  },
  addRecipients: (state, users) => {
    const ids = state.recipients.map(recipient => recipient.id)
    const newUsers = users.filter(user => !ids.includes(user.id))
    state.recipients = [...state.recipients, ...newUsers]
  },
  removeRecipients: (state, users) => {
    const ids = users.map(user => user.id)
    state.recipients = state.recipients.filter(
      recipient => !ids.includes(recipient.id)
    )
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  displayMessageModal: ({ commit }, display) => {
    commit('displayMessageModal', display)
  },
  updateHistory: async ({ commit }) => {
    const res = await Vue.$api.message.messages({})
    commit('updateHistory', res.results)
    commit('updateHistoryNext', res.next || '')
  },
  updateUnreadHistory: async ({ commit }) => {
    const res = await Vue.$api.message.messages({ isUnread: true })
    commit('updateUnreadHistory', res.results)
  },
  loadNextHistory: async ({ commit, state }) => {
    const cursor = state.history.nextCursor
    if (!cursor) return
    const res = await Vue.$api.message.messages({ cursor })
    commit('addHistory', res.results)
    commit('updateHistoryNext', res.next || '')
  },
  updateRecipients: ({ commit }, users) => {
    commit('updateRecipients', users)
  },
  addRecipients: ({ commit }, users) => {
    commit('addRecipients', users)
  },
  removeRecipients: ({ commit }, users) => {
    commit('removeRecipients', users)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
